import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { ErrorsHostDirective } from '@app/core/forms/directives/errors-host.directive';
import { IfInvalidDirective } from '@app/core/forms/directives/if-invalid.directive';
import { UserInToken } from '@app/entities/users/interfaces/user-in-token';
import { UserProfile } from '@app/entities/users/interfaces/user-profile';
import { AuthSelectors, ProfileSelectors } from '@app/store';
import { Store } from '@ngxs/store';
import { filter, map, Observable, tap } from 'rxjs';

@Component({
  selector: 'app-account-information',
  templateUrl: './account-information.component.html',
  styleUrls: ['./account-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    IfInvalidDirective,
    ErrorsHostDirective,
    AsyncPipe,
  ],
})
export class AccountInformationComponent {
  constructor(
    private store: Store,
    private formBuilder: FormBuilder,
    private destroyRef: DestroyRef,
  ) {}

  protected currentUser$: Observable<UserInToken | undefined> =
    this.store.select(AuthSelectors.currentUser);

  form!: FormGroup;
  protected userEmail$!: Observable<string | undefined>;

  protected profile$: Observable<UserProfile | undefined> = inject(
    Store,
  ).select(ProfileSelectors.profile);

  ngOnInit(): void {
    this.userEmail$ = this.getUserEmail();
    this.form = this.buildForm();
    this.populateForm();
  }

  private populateForm(): void {
    this.profile$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((value: UserProfile | undefined) => !!value),
        tap((userProfile: UserProfile) => {
          this.nameFC.setValue(userProfile.name);
          this.surnameFC.setValue(userProfile.surname);
          this.phoneFC.setValue(userProfile.phone);
        }),
      )
      .subscribe();
  }

  getUserEmail(): Observable<string | undefined> {
    return this.currentUser$.pipe(
      map((user: UserInToken | undefined) => {
        return user?.email;
      }),
    );
  }

  private buildForm(): FormGroup {
    return this.formBuilder.group({
      name: [null],
      surname: [null],
      phone: [null],
    });
  }

  protected get nameFC(): FormControl {
    return this.form.get('name') as FormControl;
  }

  protected get surnameFC(): FormControl {
    return this.form.get('surname') as FormControl;
  }

  protected get phoneFC(): FormControl {
    return this.form.get('phone') as FormControl;
  }
}
